import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { TextInput, Tooltip } from "flowbite-react";
import { Spinner } from "../components/atoms/Spinner";
import { StatusBadge } from "../components/atoms/StatusBadge";
import { Switch } from "../components/atoms/Switch";
import {
  HiSparkles,
  HiSearch,
  HiExclamationCircle,
  HiOutlineDocumentSearch,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import HttpService from "../services/http";
import AiDocumentTemplates from "../components/molecules/AiDocumentTemplates";
import { CustomButton } from "../components/atoms/CustomButton";
import { EmptyState } from "../components/atoms/EmptyState";
import { usePermissions, PlanTier } from "../utils/permissions";
import { PlansModal } from "../components/modals/PlansModal";
import AiImportHowItWorks from "../components/pages/aiConfig/AiImportHowItWorks";
import AiSearchHowItWorks from "../components/pages/aiConfig/AiSearchHowItWorks";
import AiUpgradeCta from "../components/pages/aiConfig/AiUpgradeCta";
import CollapsibleHowItWorks from "../components/common/CollapsibleHowItWorks";

type Dimension = {
  umd_path: string;
  label?: string;
  is_selected: boolean;
  is_orphan: boolean;
};

type DimensionsPayload = {
  dimensions: Dimension[];
  counts: { total: number; selected: number; orphan: number };
};

const AiConfig: React.FC = () => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const entityIdentification = user?.entity_identification;
  const base = `${customer_identification}/${entityIdentification}/user`;

  const permissions = usePermissions(user);
  const hasAiAccess = permissions.hasFeatureAccess("AI_ENGINE");
  const [showPlansModal, setShowPlansModal] = useState(false);

  const [activeTab, setActiveTab] = useState<"search" | "import">("search");

  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [filter, setFilter] = useState("");
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const [savingLogging, setSavingLogging] = useState(false);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await HttpService.get<{ element: DimensionsPayload }>(
        `${base}/ai-dimensions`
      );
      if (!aliveRef.current) return;
      const el = res.data?.element;
      const dims = el?.dimensions ?? [];
      setDimensions(dims);
      const next: Record<string, boolean> = {};
      dims.forEach((d) => {
        next[d.umd_path] = d.is_selected;
      });
      setSelection(next);
    } catch {
      if (!aliveRef.current) return;
      setDimensions([]);
      setSelection({});
      toast.error(t("dashboard.aiConfig.loadError") as string);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [base, t]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await HttpService.get<{
        element: { logging_enabled: boolean };
      }>(`${base}/ai-search-settings`);
      if (!aliveRef.current) return;
      setLoggingEnabled(res.data?.element?.logging_enabled !== false);
    } catch {
      if (aliveRef.current) setLoggingEnabled(true);
    }
  }, [base]);

  const toggleLogging = async (next: boolean) => {
    setLoggingEnabled(next);
    setSavingLogging(true);
    try {
      await HttpService.post(`${base}/ai-search-settings`, {
        logging_enabled: next,
      });
    } catch {
      setLoggingEnabled(!next);
      toast.error(t("dashboard.aiConfig.settingsError") as string);
    } finally {
      setSavingLogging(false);
    }
  };

  useEffect(() => {
    if (entityIdentification && hasAiAccess) {
      load();
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [entityIdentification, hasAiAccess, load, loadSettings]);

  const toggle = (path: string) =>
    setSelection((prev) => ({ ...prev, [path]: !prev[path] }));

  const handleApply = async () => {
    setApplying(true);
    try {
      const selected = Object.keys(selection).filter((p) => selection[p]);
      const res = await HttpService.post<{
        element: { upserted: number; deleted: number };
      }>(`${base}/ai-dimensions/apply`, { selected });
      const el = res.data?.element;
      toast.success(
        t("dashboard.aiConfig.applyDone", {
          count: el?.upserted ?? selected.length,
        }) as string
      );
      await load();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          (t("dashboard.aiConfig.applyError") as string)
      );
    } finally {
      setApplying(false);
    }
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return dimensions;
    return dimensions.filter(
      (d) =>
        d.umd_path.toLowerCase().includes(f) ||
        (d.label || "").toLowerCase().includes(f)
    );
  }, [dimensions, filter]);

  const selectedCount = useMemo(
    () => Object.values(selection).filter(Boolean).length,
    [selection]
  );
  const orphanCount = useMemo(
    () => dimensions.filter((d) => d.is_orphan).length,
    [dimensions]
  );

  const isDirty = useMemo(
    () =>
      dimensions.some((d) => Boolean(selection[d.umd_path]) !== d.is_selected),
    [dimensions, selection]
  );

  return (
    <div className="ai-config-page">
      <Helmet>
        <title>{t("dashboard.aiConfig.title")} | Super User Manager</title>
      </Helmet>

      <div className="container mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("dashboard.aiConfig.title")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("dashboard.aiConfig.subtitle")}
            </p>
          </div>
        </div>

        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("search")}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                activeTab === "search"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <HiSearch size={18} />
              {t("dashboard.aiConfig.tabs.search")}
            </button>
            <button
              onClick={() => setActiveTab("import")}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                activeTab === "import"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <HiOutlineDocumentSearch size={18} />
              {t("dashboard.aiConfig.tabs.import")}
            </button>
          </nav>
        </div>

        {activeTab === "search" && !hasAiAccess && (
          <>
            <CollapsibleHowItWorks
              collapsible={false}
              title={t("dashboard.aiConfig.howItWorksToggle")}
              icon={<HiSparkles className="text-gray-500" size={18} />}
            >
              <AiSearchHowItWorks />
            </CollapsibleHowItWorks>
            <AiUpgradeCta onUpgrade={() => setShowPlansModal(true)} />
          </>
        )}

        {activeTab === "search" && hasAiAccess && (
          <>
            <CollapsibleHowItWorks
              collapsible={true}
              title={t("dashboard.aiConfig.howItWorksToggle")}
              icon={<HiSparkles className="text-gray-500" size={18} />}
              className="mb-6"
            >
              <AiSearchHowItWorks />
            </CollapsibleHowItWorks>

            <p className="mb-4 text-sm text-gray-500">
              {t("dashboard.aiConfig.tabs.searchIntro")}
            </p>

            <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("dashboard.aiConfig.dimensionsTitle")}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {t("dashboard.aiConfig.selectedCount", {
                      selected: selectedCount,
                      total: dimensions.length,
                    })}
                    {orphanCount > 0 && (
                      <span className="ml-2 text-amber-600">
                        ·{" "}
                        {t("dashboard.aiConfig.orphanCount", {
                          count: orphanCount,
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <TextInput
                  sizing="sm"
                  icon={HiSearch}
                  placeholder={
                    t("dashboard.aiConfig.filterPlaceholder") as string
                  }
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-64 max-w-full shrink-0"
                />
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : filtered.length === 0 ? (
                <EmptyState
                  icon={<HiOutlineDocumentSearch size={24} />}
                  title={
                    dimensions.length === 0
                      ? t("dashboard.aiConfig.empty")
                      : t("dashboard.aiConfig.noMatch")
                  }
                />
              ) : (
                <div className="max-h-[60vh] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                  {filtered.map((d) => {
                    const checked = Boolean(selection[d.umd_path]);
                    return (
                      <div
                        key={d.umd_path}
                        className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm text-gray-700">
                              {d.label || d.umd_path}
                            </div>
                          </div>
                          {d.is_orphan && (
                            <Tooltip
                              content={
                                t("dashboard.aiConfig.orphanHint") as string
                              }
                            >
                              <StatusBadge
                                color="warning"
                                size="xs"
                                icon={HiExclamationCircle}
                                className="shrink-0 whitespace-nowrap [&>svg]:m-0 md:[&>svg]:mr-1"
                              >
                                <span className="hidden md:inline">
                                  {t("dashboard.aiConfig.orphanBadge")}
                                </span>
                              </StatusBadge>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Switch
                            checked={checked}
                            onChange={() => toggle(d.umd_path)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 flex justify-end border-t border-gray-100 pt-4">
                <CustomButton
                  variant="primary"
                  icon="save"
                  title={t("dashboard.aiConfig.applyBtn")}
                  onClick={handleApply}
                  disabled={applying || !isDirty}
                  isLoading={applying}
                  className="shrink-0"
                />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("dashboard.aiConfig.loggingTitle")}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {t("dashboard.aiConfig.loggingHint")}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {savingLogging && <Spinner size="sm" />}
                  <Switch
                    checked={loggingEnabled}
                    onChange={toggleLogging}
                    disabled={savingLogging}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "import" && (
          <>
            <CollapsibleHowItWorks
              collapsible={hasAiAccess}
              title={t("dashboard.aiConfig.howItWorksToggle")}
              icon={<HiSparkles className="text-gray-500" size={18} />}
              className="mb-6"
            >
              <AiImportHowItWorks />
            </CollapsibleHowItWorks>
            {hasAiAccess ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  {t("dashboard.aiConfig.import.templatesIntro")}
                </p>
                <AiDocumentTemplates base={base} />
              </>
            ) : (
              <AiUpgradeCta onUpgrade={() => setShowPlansModal(true)} />
            )}
          </>
        )}
      </div>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
      />
    </div>
  );
};

export default AiConfig;
